/**
 * Replay playback controls.
 * Play/pause, speed selector, step forward/backward buttons.
 */

import { Play, Pause, SkipBack, SkipForward } from 'lucide-react';
import { useReplayStore } from '@/stores/replay-store';
import { REPLAY_SPEEDS } from '@/constants/drawing';

/** Playback control buttons for track replay */
export function ReplayControls(): React.JSX.Element {
  const { playing, speed, play, pause, setSpeed, seekTo, currentIndex, frames } = useReplayStore();
  const maxIndex = Math.max(0, frames.length - 1);

  function stepBack(): void {
    seekTo(Math.max(0, currentIndex - 1));
  }

  function stepForward(): void {
    seekTo(Math.min(maxIndex, currentIndex + 1));
  }

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1">
        <button onClick={stepBack} className="rounded p-1.5 text-foreground hover:bg-white/[0.1]" title="Step backward">
          <SkipBack size={14} />
        </button>

        <button
          onClick={playing ? pause : play}
          className="rounded bg-echo-yellow p-2 text-black"
          title={playing ? 'Pause' : 'Play'}
        >
          {playing ? <Pause size={14} /> : <Play size={14} />}
        </button>

        <button onClick={stepForward} className="rounded p-1.5 text-foreground hover:bg-white/[0.1]" title="Step forward">
          <SkipForward size={14} />
        </button>
      </div>

      <div className="flex items-center gap-1">
        {REPLAY_SPEEDS.map((s) => (
          <button
            key={s}
            onClick={() => setSpeed(s)}
            className={`rounded px-2 py-1 text-[10px] font-medium ${
              speed === s
                ? 'bg-echo-yellow text-black'
                : 'bg-white/[0.06] text-muted-foreground hover:text-foreground'
            }`}
          >
            {s}x
          </button>
        ))}
      </div>
    </div>
  );
}
